import { useFormik } from "formik";
import { Button } from "primereact/button";
import React from "react";
import { useFragment } from "react-relay";
import { match } from "ts-pattern";
import { useTranslation } from "@i18n/hooks";
import { textWithPagesElementPageForm_TextWithPagesPageFragment$key } from "@relay/textWithPagesElementPageForm_TextWithPagesPageFragment.graphql";
import { TextWithPagesPageDataKind } from "@relay/textWithPagesElementPagesEditor_PagesFragment.graphql";
import { createTextWithPagesElementPageFormSchema } from "./text-with-pages-element-page-form.consts";
import { TEXT_WITH_PAGES_PAGE_FRAGMENT } from "./text-with-pages-element-page-form.graphql";
import { buttonWrapperClass } from "./text-with-pages-element-page-form.styles";
import {
	TextWithPagesElementPageFormProps,
	TextWithPagesElementPageFormState,
} from "./text-with-pages-element-page-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { TextWithPagesPageKindDropdown } from "../../../../components/text-with-pages-element-page-kind-dropdown";
import { ValidatedField } from "../../../../components/ValidatedField";
import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";

export const TextWithPagesElementPageForm = ({
	textWithPagesElementPageFragmentRef,
	onSubmit,
	isCreating,
}: TextWithPagesElementPageFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const page = useFragment<textWithPagesElementPageForm_TextWithPagesPageFragment$key>(
		TEXT_WITH_PAGES_PAGE_FRAGMENT,
		textWithPagesElementPageFragmentRef ?? null,
	);

	const initialData = {
		internalTitle: page?.internalTitle ?? "",
		text: page?.data.text ?? "",
		image: {
			id: page?.data.image?.id ?? "",
			name: page?.data.image?.name ?? "",
		},
		kind: page?.data.kind ?? "TextPageData",
	};

	const formik = useFormik<TextWithPagesElementPageFormState>({
		enableReinitialize: false,
		initialValues: initialData,
		validationSchema: createTextWithPagesElementPageFormSchema(t),
		onSubmit: (values) => {
			onSubmit?.(values);
		},
	});

	const displayPageDataInputField = (): JSX.Element => {
		return match(formik.values.kind as TextWithPagesPageDataKind)
			.with("TextPageData", () => (
				<ValidatedField<TextWithPagesElementPageFormState, string>
					name={"text"}
					label={t("forms.text")}
					required
					formikConfig={formik}
					component={(props) => (
						<MemoizedRichTextEditor
							{...props}
							height={200}
							toolbar={RichTextEditorToolbar.WithLists}
						/>
					)}
				/>
			))
			.with("ImagePageData", () => (
				<ValidatedField<TextWithPagesElementPageFormState, FileV2>
					name={"image"}
					label={t("forms.image")}
					required
					formikConfig={formik}
					component={({ fieldValue, updateField }) => (
						<FileSelectionField
							name={"element"}
							selectedFile={fieldValue}
							setSelectedFile={updateField}
							filterByFileTypes={["image/png", "image/jpg", "image/jpeg"]}
							canUploadFiles={true}
							canDeleteFiles={true}
						/>
					)}
				/>
			))
			.exhaustive();
	};

	return (
		<form onSubmit={formik.handleSubmit}>
			<ValidatedField<TextWithPagesElementPageFormState, TextWithPagesPageDataKind>
				disabled={!isCreating}
				name={"kind"}
				label={t("forms.pageType")}
				required
				component={TextWithPagesPageKindDropdown}
				formikConfig={formik}
			/>
			<ValidatedField<TextWithPagesElementPageFormState, string>
				name={"internalTitle"}
				label={t("forms.internalTitle")}
				required
				component={DefaultTextFieldComponent}
				formikConfig={formik}
			/>
			{displayPageDataInputField()}
			<div className={buttonWrapperClass}>
				<Button type="submit" label={t("actions.save")} />
			</div>
		</form>
	);
};
